import { poisEndpoints } from '@ooh/contracts';
import type { EndpointDef, callEndpoint } from '@ooh/contracts';

// ─── Shared auth interface ────────────────────────────────────

export interface WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

// ─── POI list filter params ───────────────────────────────────

export interface PoiListFilters {
  bbox?: string;
  source?: 'osm' | 'custom';
  osmKey?: string;
  osmValue?: string;
  internalOnly?: boolean;
  includeDeleted?: boolean;
}

// ─── Stable key prefix (used by mutations for invalidation) ──

export const POI_LIST_KEY_PREFIX = ['pois', 'list'] as const;

// ─── Query factories ──────────────────────────────────────────

export const poiQueries = {
  // Returns Poi[] directly (endpoint responseSchema is z.array(PoiSchema))
  list: (auth: WithCallWithAuth, filters: PoiListFilters = {}) => ({
    queryKey: ['pois', 'list', filters] as const,
    queryFn: () =>
      auth.callWithAuth(poisEndpoints.list, {
        bbox: filters.bbox,
        source: filters.source,
        osm_key: filters.osmKey,
        osm_value: filters.osmValue,
        internal_only: filters.internalOnly,
        include_deleted: filters.includeDeleted,
      }),
  }),

  // Returns Poi directly
  byId: (auth: WithCallWithAuth, id: string) => ({
    queryKey: ['pois', id] as const,
    queryFn: () => auth.callWithAuth(poisEndpoints.getById, { id }),
  }),

  // Returns PoiCategory[] directly
  categories: (auth: WithCallWithAuth) => ({
    queryKey: ['poi-categories'] as const,
    queryFn: () => auth.callWithAuth(poisEndpoints.categories, {}),
  }),

  // Returns MapPinsResponse: { data: MapPin[] }
  mediaUnitsWithin: (auth: WithCallWithAuth, poiId: string, radiusM: number) => ({
    queryKey: ['pois', poiId, 'media-units-within', radiusM] as const,
    queryFn: () =>
      auth.callWithAuth(poisEndpoints.mediaUnitsWithin, { id: poiId, radius_m: radiusM }),
  }),

  // Returns PoiStats (admin-only)
  stats: (auth: WithCallWithAuth) => ({
    queryKey: ['pois', 'stats'] as const,
    queryFn: () => auth.callWithAuth(poisEndpoints.stats, {}),
  }),

  // Returns PoiImportStatus (admin-only)
  importStatus: (auth: WithCallWithAuth) => ({
    queryKey: ['pois', 'import', 'status'] as const,
    queryFn: () => auth.callWithAuth(poisEndpoints.importStatus, {}),
  }),
};

export const mediaUnitPoiQueries = {
  // Returns NearbyPoi[] directly
  nearbyPois: (auth: WithCallWithAuth, mediaUnitId: string, limit: number) => ({
    queryKey: ['pois', mediaUnitId, 'nearby-pois', limit] as const,
    queryFn: () => auth.callWithAuth(poisEndpoints.nearbyPois, { id: Number(mediaUnitId), limit }),
  }),
};
